import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import { FaArrowRight, FaDownload, FaFilePdf } from 'react-icons/fa';

const InsightsPage = () => {
  const seoData = {
    title: "AI Symbiosis: Forging Human-Machine Partnerships - Insights and Downloads",
    description: "Explore how AI is reshaping the workforce through collaborative partnerships between humans, intelligent agents, and robotic systems, potentially unlocking $3.5 trillion in annual U.S. economic value by 2030.",
    keywords: "AI Symbiosis, Human-Machine Partnerships, AI Workforce, AI Collaboration, AI Skills, Workflow Redesign, AI Transformation"
  };

  return (
    <div className="bg-white text-gray-900 font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO {...seoData} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-purple-50 to-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              AI Symbiosis: Forging Human-Machine Partnerships for Enduring Skills and Exponential Growth
            </h1>

            <div className="flex items-center gap-4 text-gray-600 mb-8">
              <span className="text-sm font-medium">Insights and Downloads</span>
              <span className="text-sm">•</span>
              <span className="text-sm">Research Report</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12 rounded-xl overflow-hidden shadow-lg"
          >
            <img 
              src="/assets/Insights.png" 
              alt="AI Symbiosis Insights" 
              className="w-full h-auto"
              loading="lazy"
            />
          </motion.div>

          {/* Key Insights Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-12 bg-gradient-to-r from-purple-50 to-blue-50 p-8 rounded-xl border-l-4 border-purple-600"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">Key Insights</h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="text-purple-600 font-bold mr-3 mt-1">●</span>
                <span>
                  <strong>AI is reshaping the workforce</strong> not through replacement, but via collaborative partnerships between humans, intelligent agents, and robotic systems, potentially unlocking <strong>$3.5 trillion in annual U.S. economic value by 2030</strong> through productivity gains.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 font-bold mr-3 mt-1">●</span>
                <span>
                  <strong>Core human skills</strong> like creativity, empathy, and strategic judgment will persist and evolve, while <strong>AI fluency demands have surged eightfold in job postings since 2023</strong>, commanding a <strong>25-30% salary premium</strong>.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 font-bold mr-3 mt-1">●</span>
                <span>
                  <strong>Workflow redesign</strong>—integrating AI as a co-pilot rather than a tool—drives the bulk of value, with case studies showing <strong>20-50% efficiency improvements</strong> in sectors from healthcare to finance.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 font-bold mr-3 mt-1">●</span>
                <span>
                  <strong>Leaders must prioritize</strong> reskilling, ethical integration, and systemic education reforms to ensure equitable benefits, as evidence suggests AI narrows skill gaps but accelerates disruptions for entry-level roles.
                </span>
              </li>
            </ul>
          </motion.div>

          {/* The Promise of Partnership */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">The Promise of Partnership</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              In an era where AI agents handle complex data synthesis and robots execute precise physical tasks, the future of work hinges on symbiosis. Research indicates that while <strong>12% of U.S. jobs face high automation risk today</strong>, <strong>human-AI collaboration boosts overall productivity by up to 40% in hybrid teams</strong>. This article explores how organizations can cultivate these alliances, drawing on recent studies to outline skill shifts, redesign strategies, and leadership imperatives.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg my-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">Why Now?</h3>
              <p className="text-gray-700 leading-relaxed">
                With <strong>global AI adoption projected to add $15-20 trillion to the economy by 2030</strong>, the urgency is clear: without proactive adaptation, disparities in skills and access could widen. Yet, the evidence leans toward opportunity—<strong>AI-exposed occupations have seen 15% faster revenue growth</strong>, suggesting a pathway to shared prosperity if navigated thoughtfully.
              </p>
            </div>
          </motion.div>

          {/* Executive Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-12 bg-gray-50 p-8 rounded-xl"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Symbiosis in the AI Era: Human-Machine Alliances for Skill Evolution and Exponential Growth
            </h2>
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Executive Summary</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The integration of artificial intelligence (AI) into the fabric of work is not a tale of obsolescence but one of augmentation and alliance. As intelligent agents—software entities capable of autonomous decision-making in digital realms—and robotic systems for physical execution proliferate, the workforce of tomorrow will thrive on partnerships that leverage human ingenuity alongside machine precision. Drawing from analyses of <strong>over 10 million job postings and 200+ enterprise workflows</strong>, this report posits that such symbioses could generate up to <strong>$3.5 trillion in annual value for the U.S. economy by 2030</strong>, primarily through enhanced productivity and innovative skill ecosystems.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              However, realizing this potential demands a paradigm shift: from viewing AI as a disruptor to embracing it as a collaborator. Human skills in empathy, ethical reasoning, and creative synthesis remain irreplaceable, even as routine tasks yield to automation. Job market data reveals a <strong>sevenfold increase in AI-related skill requirements since 2023</strong>, with <strong>1.8% of postings now mandating them</strong>—up from under 1% a decade ago. Yet, controversy lingers: while AI may displace <strong>85-100 million jobs globally by 2030</strong>, it is projected to create <strong>97 million new ones</strong>, often in hybrid roles that blend technical and interpersonal proficiencies.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This report, informed by interdisciplinary research from sources including MIT, PwC, and the World Economic Forum, charts a course through four pillars: the architecture of symbiotic workforces, the fluidity of human skills, the reengineering of workflows, and the stewardship required for equitable transition. It underscores that success favors the prepared—<strong>organizations investing in AI fluency and collaborative redesign report 25% higher employee retention and 30% productivity lifts</strong>. For leaders, educators, and policymakers, the message is unequivocal: foster alliances today to harvest abundance tomorrow.
            </p>
          </motion.div>

          {/* Chapter 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Chapter 1: Architecting the Symbiotic Workforce</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The modern workplace is evolving from silos of human labor to ecosystems where people, AI agents, and robots co-orchestrate outcomes. Consider the spectrum: purely human-centric roles (e.g., counseling, where empathy reigns) comprise about <strong>25% of U.S. work hours</strong>, while agent-heavy domains like data analysis dominate <strong>45%</strong>, and robot-intensive manufacturing accounts for <strong>30%</strong>. Hybrid archetypes—such as "human-agent" teams in marketing or "human-robot" pairings in assembly lines—are emerging as the norm, with studies showing <strong>70% performance gains when AI augments rather than supplants human effort</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              A pivotal framework here is the <strong>Symbiosis Index (SI)</strong>, which quantifies collaboration potential across occupations. Derived from occupational data in the Bureau of Labor Statistics (BLS) and O*NET, alongside AI capability mappings via models like GPT-4, the SI reveals that <strong>65% of tasks in knowledge work benefit from agent integration</strong>, while physical roles see <strong>40% uplift from robotic assistance</strong>. For instance, in logistics, human-robot teams reduce error rates by <strong>35% through real-time feedback loops</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Yet, challenges persist. Technological hurdles, such as agents' limitations in nuanced judgment or robots' dexterity constraints, mean humans retain oversight in <strong>80% of high-stakes scenarios</strong>. Ethical debates also surface: does over-reliance on AI erode human agency? Evidence from field experiments suggests not—<strong>collaborative setups enhance job satisfaction by 22% when humans guide AI outputs</strong>.
            </p>

            {/* Exhibit 1 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 overflow-x-auto">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Exhibit 1: Symbiosis Spectrum Across Occupations</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Archetype</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">% of Work Hours</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Example Roles</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Productivity Gain from Hybrid</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Human-Centric</td>
                      <td className="px-4 py-3 text-sm text-gray-700">25%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Therapists, Strategists</td>
                      <td className="px-4 py-3 text-sm text-gray-700">N/A (Baseline)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">Agent-Centric</td>
                      <td className="px-4 py-3 text-sm text-gray-700">45%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Analysts, Coders</td>
                      <td className="px-4 py-3 text-sm text-gray-700">+40% with Human Oversight</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Robot-Centric</td>
                      <td className="px-4 py-3 text-sm text-gray-700">30%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Welders, Packers</td>
                      <td className="px-4 py-3 text-sm text-gray-700">+35% with Human Direction</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">Human-Agent</td>
                      <td className="px-4 py-3 text-sm text-gray-700">60% (Hybrid Share)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Marketers, Consultants</td>
                      <td className="px-4 py-3 text-sm text-gray-700">+70% Collaborative</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Human-Robot</td>
                      <td className="px-4 py-3 text-sm text-gray-700">50% (Hybrid Share)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Surgeons, Mechanics</td>
                      <td className="px-4 py-3 text-sm text-gray-700">+25% Precision Boost</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4 italic">Source: Adapted from MIT Sloan and Accenture studies on AI integration.</p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              This spectrum illustrates not displacement, but elevation: AI handles the mundane, freeing humans for innovation. As one Upwork study notes, <em>"AI agents fail solo but flourish with human partners,"</em> underscoring the relational core of future work.
            </p>
          </motion.div>

          {/* Chapter 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Chapter 2: Skills in Flux: From Erosion to Augmentation</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Contrary to fears of skill obsolescence, AI catalyzes evolution. Analysis of <strong>12 million U.S. job postings from 2020-2025</strong> shows the average skills per role rising from <strong>52 to 68</strong>, with <strong>AI fluency—encompassing prompt engineering and ethical AI use—appearing in 2.1% of listings</strong>, a <strong>900% jump since ChatGPT's debut</strong>. These skills command a <strong>28% wage premium</strong>, particularly in non-tech fields like marketing (50% YoY demand growth) and healthcare.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Transferable competencies endure: communication, critical thinking, and adaptability feature in <strong>75% of postings</strong> across AI-exposed and insulated roles. The <strong>Skill Flux Index (SFI)</strong>, blending Lightcast data with PwC's AI Jobs Barometer, indicates that skills in AI-vulnerable sectors (e.g., administrative support) shift <strong>65% faster than others</strong>, favoring augmentation over atrophy. Digital natives face the steepest curve, with <strong>15% of entry-level postings now requiring AI basics</strong>, while caring professions like nursing show minimal flux (under 10%).
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Controversially, while AI narrows skill gaps—boosting low-skill worker output by <strong>14%</strong>—it may hinder deep learning if over-relied upon, per Georgetown's CSET research. Mitigation lies in hybrid training: programs blending AI tools with human mentorship yield <strong>30% better retention of complex skills</strong>.
            </p>

            {/* Exhibit 2 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 overflow-x-auto">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Exhibit 2: Skill Demand Trends (2020-2025)</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Skill Category</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">% Postings Requiring (2025)</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Growth Since 2020</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Salary Premium</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">AI Fluency</td>
                      <td className="px-4 py-3 text-sm text-gray-700">2.1%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">900%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">28%</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Communication</td>
                      <td className="px-4 py-3 text-sm text-gray-700">75%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stable</td>
                      <td className="px-4 py-3 text-sm text-gray-700">15%</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Data Analysis</td>
                      <td className="px-4 py-3 text-sm text-gray-700">40%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">200%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">22%</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Ethical Reasoning</td>
                      <td className="px-4 py-3 text-sm text-gray-700">18%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">300%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">20%</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Routine Coding</td>
                      <td className="px-4 py-3 text-sm text-gray-700">12%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-15% (Decline)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">N/A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4 italic">Source: Lightcast and PwC AI Jobs Barometer.</p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              In essence, skills are not vanishing; they are hybridizing, demanding lifelong learning ecosystems that value both tech savvy and human depth.
            </p>
          </motion.div>

          {/* Chapter 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Chapter 3: Reengineering Workflows for Collaborative Intelligence</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Value accrues not from AI adoption alone, but from holistic workflow redesign. McKinsey's examination of <strong>25 organizational attributes</strong> pinpoints redesign as the top driver of EBIT impact, outpacing model sophistication by <strong>2x</strong>. Across <strong>150 workflows in finance, healthcare, and manufacturing</strong>, <strong>55% of gains stem from sector-tailored integrations</strong>, such as AI agents triaging patient queries to cut diagnostic wait times by <strong>40%</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Case studies illuminate the path:
            </p>

            <div className="space-y-6 mb-6">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Healthcare Diagnostics</h3>
                <p className="text-gray-700 leading-relaxed">
                  A Midwestern hospital deployed human-AI teams where agents pre-analyze scans, humans validate—reducing errors <strong>45%</strong> and freeing clinicians for patient interaction, yielding <strong>$12M annual savings</strong>.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Financial Services</h3>
                <p className="text-gray-700 leading-relaxed">
                  A bank reimagined loan processing with agent-robot hybrids; AI assesses risk, robots handle document routing—productivity rose <strong>35%</strong>, costs fell <strong>28%</strong>.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Retail Operations</h3>
                <p className="text-gray-700 leading-relaxed">
                  An e-commerce giant's supply chain workflow integrated predictive agents with human strategists, boosting inventory accuracy <strong>50%</strong> and revenue <strong>18%</strong>.
                </p>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Creative Industries</h3>
                <p className="text-gray-700 leading-relaxed">
                  Marketing firms using GenAI for content ideation report <strong>25% faster campaign cycles</strong>, with humans refining for brand nuance—though motivation dips <strong>12%</strong> without clear guidelines.
                </p>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Managerial roles pivot to "orchestrators," spending <strong>60% less time on supervision</strong> and more on innovation. The caveat: poor redesign risks "AI fatigue," with <strong>20% productivity loss from misaligned tools</strong>.
            </p>

            {/* Exhibit 3 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 overflow-x-auto">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Exhibit 3: Workflow Redesign Impact by Sector</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Sector</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">% Workflows Reimagined</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Avg. Productivity Gain</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Savings</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Healthcare</td>
                      <td className="px-4 py-3 text-sm text-gray-700">62%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">40%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">35%</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Finance</td>
                      <td className="px-4 py-3 text-sm text-gray-700">58%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">35%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">28%</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Manufacturing</td>
                      <td className="px-4 py-3 text-sm text-gray-700">55%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">30%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">25%</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">Retail</td>
                      <td className="px-4 py-3 text-sm text-gray-700">50%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">25%</td>
                      <td className="px-4 py-3 text-sm text-gray-700">20%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4 italic">Source: Aggregated from Evident and HBR case analyses.</p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              These examples affirm: redesign is the multiplier, turning AI from cost center to value engine.
            </p>
          </motion.div>

          {/* Chapter 4 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Chapter 4: Stewardship for an Inclusive Transition</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Leadership is the linchpin. As AI accelerates skill shifts—<strong>66% faster in exposed jobs</strong>—executives must champion experimentation, with <strong>70% of high-impact firms piloting hybrid teams quarterly</strong>. Ethical imperatives loom large: build trust through transparent AI governance, as <strong>55% of workers cite bias fears as adoption barriers</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              For institutions, integrate AI literacy from K-12, per WEF recommendations, to counter <strong>6% employment dips for young workers in high-exposure fields</strong>. Public-private pacts, akin to post-WWII reskilling, could amplify this: credentialing transferable skills and subsidizing upskilling for <strong>50 million workers by 2030</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              The horizon holds promise, tempered by equity concerns. AI's GDP boost—<strong>0.5% annually through 2030</strong>—must be democratised, lest it exacerbate divides. Leaders who prioritize inclusive alliances will not only capture value but redefine work as a human-centred endeavour.
            </p>
          </motion.div>

          {/* Key Citations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mb-12 bg-gray-50 p-8 rounded-xl"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">Key Citations</h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>MIT Study on AI Workforce Replacement</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>Stanford AI Index 2025</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>IDC AI Economic Value Projection</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>Lightcast AI Skills Report</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>Upwork AI Collaboration Study</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>PwC AI Jobs Barometer</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>McKinsey Workflow Redesign Insights</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>World Economic Forum Future of Jobs 2025</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>HBR GenAI Productivity Research</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3 mt-1">•</span>
                <span>IMF AI Economics Projections</span>
              </li>
            </ul>
          </motion.div>

          {/* Download CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-8 rounded-xl text-center mb-12"
          >
            <FaFilePdf className="text-5xl mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">Download Full Report</h3>
            <p className="text-lg mb-6 opacity-90">
              Get the complete research report with detailed analysis, data visualizations, and actionable insights.
            </p>
            <button className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors">
              <FaDownload />
              Download PDF
            </button>
          </motion.div>

          {/* Navigation Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="flex justify-center items-center gap-4 pt-8 border-t border-gray-200"
          >
            <Link 
              to="/contact" 
              className="inline-flex items-center bg-purple-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors"
            >
              Contact Us
              <FaArrowRight className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;

